import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage } from '../types/chat.types';

const SESSION_STORAGE_KEY = 'cribsy_chat_session_id';
const MESSAGES_STORAGE_KEY = 'cribsy_chat_messages';

export const useChatSession = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Initialize or retrieve session on mount
  useEffect(() => {
    const storedSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    const storedMessages = localStorage.getItem(MESSAGES_STORAGE_KEY);

    if (storedSessionId) {
      setSessionId(storedSessionId);
      if (storedMessages) {
        try {
          setMessages(JSON.parse(storedMessages));
        } catch (e) {
          console.error('Failed to parse stored messages:', e);
          setMessages([]);
        }
      }
    } else {
      // Create new session
      const newSessionId = uuidv4();
      setSessionId(newSessionId);
      localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
    }
  }, []);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(MESSAGES_STORAGE_KEY);
  }, []);

  const resetSession = useCallback(() => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    setMessages([]);
    localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
    localStorage.removeItem(MESSAGES_STORAGE_KEY);
  }, []);

  const loadMessages = useCallback((loadedMessages: ChatMessage[]) => {
    setMessages(loadedMessages);
  }, []);

  return {
    sessionId,
    messages,
    addMessage,
    clearMessages,
    resetSession,
    loadMessages,
  };
};
